import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, BarChart3, Camera, Users, Download } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { eventsService, statsService } from '../../services/firestoreService';
import { db } from '../../firebase/config';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';

// Util: YYYY-MM-DD
const toDayKey = (d) => {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const daysRange = (n) => {
  const arr = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    arr.push(toDayKey(d));
  }
  return arr;
};

export const Reports = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [kpis, setKpis] = useState({ totalEvents: 0, activeEvents: 0, inactiveEvents: 0, totalPhotos: 0, totalParticipants: 0 });
  const [trendDays] = useState(14);
  const [trend, setTrend] = useState({ labels: [], counts: [] });
  const [statusCounts, setStatusCounts] = useState({ active: 0, inactive: 0 });

  useEffect(() => {
    if (!user?.uid) return;
    const load = async () => {
      try {
        setLoading(true);
        const [evts, stats] = await Promise.all([
          eventsService.getOrganizerEvents(user.uid),
          statsService.getOrganizerStats(user.uid)
        ]);
        setEvents(evts);
        setKpis({
          totalEvents: stats.totalEvents,
          activeEvents: stats.activeEvents || evts.filter(e => e.status === 'active').length,
          inactiveEvents: evts.filter(e => e.status !== 'active').length,
          totalPhotos: stats.totalPhotos,
          totalParticipants: stats.totalParticipants
        });
        // Estado (active/inactive)
        const counts = { active: 0, inactive: 0 };
        evts.forEach(e => {
          if (e.status === 'active') counts.active++; else counts.inactive++;
        });
        setStatusCounts(counts);

        // Trend últimos N días: consultar por evento y sumar por día
        const since = new Date();
        since.setDate(since.getDate() - (trendDays - 1));
        const sinceTs = since; // Date, convert on query by Firestore auto conversion (SDK accepts Date)
        const dayKeys = daysRange(trendDays);
        const bucket = Object.fromEntries(dayKeys.map(k => [k, 0]));

        // Consultar por evento para evitar límites de 'in'
        for (const ev of evts) {
          try {
            const q = query(
              collection(db, 'photos'),
              where('eventId', '==', ev.eventId || ev.id),
              where('uploadedAt', '>=', sinceTs),
              orderBy('uploadedAt', 'asc')
            );
            const snap = await getDocs(q);
            snap.docs.forEach(doc => {
              const dt = doc.data().uploadedAt?.toDate ? doc.data().uploadedAt.toDate() : null;
              if (!dt) return;
              const key = toDayKey(dt);
              if (bucket[key] !== undefined) bucket[key] += 1;
            });
          } catch (_) { /* ignore individual failures */ }
        }
        setTrend({ labels: dayKeys, counts: dayKeys.map(k => bucket[k]) });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.uid, trendDays]);

  const topEvents = useMemo(() => {
    const sorted = [...events].sort((a, b) => (b.totalPhotos || 0) - (a.totalPhotos || 0));
    return sorted.slice(0, 5);
  }, [events]);

  const exportCSV = () => {
    const rows = [
      ['Evento', 'Estado', 'Fecha', 'Ubicación', 'Fotos', 'Participantes'],
      ...events.map(e => [
        e.name || '',
        e.status || '',
        e.date?.toDate ? e.date.toDate().toISOString() : (e.date || ''),
        e.location || '',
        String(e.totalPhotos || 0),
        String(e.totalParticipants || 0)
      ])
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replaceAll('"', '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'muchpic_reportes.csv';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  const maxTrend = Math.max(1, ...trend.counts);
  const totalByStatus = Object.values(statusCounts).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Reportes</h1>
          <p className="text-gray-600">Resumen y analíticas de tus eventos</p>
        </div>
        <button onClick={exportCSV} className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 flex items-center">
          <Download className="w-4 h-4 mr-2" /> Exportar CSV
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card p-5">
          <div className="flex items-center gap-3 text-gray-700"><BarChart3 className="w-5 h-5" /> Eventos</div>
          <div className="mt-2 text-3xl font-semibold">{kpis.totalEvents}</div>
          <div className="text-sm text-gray-500">Activos: {kpis.activeEvents} · Inactivos: {kpis.inactiveEvents}</div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-3 text-gray-700"><Camera className="w-5 h-5" /> Fotos compartidas</div>
          <div className="mt-2 text-3xl font-semibold">{kpis.totalPhotos}</div>
          <div className="text-sm text-gray-500">Promedio por evento: {kpis.totalEvents ? Math.round(kpis.totalPhotos / kpis.totalEvents) : 0}</div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-3 text-gray-700"><Users className="w-5 h-5" /> Participantes</div>
          <div className="mt-2 text-3xl font-semibold">{kpis.totalParticipants}</div>
          <div className="text-sm text-gray-500">Promedio por evento: {kpis.totalEvents ? Math.round(kpis.totalParticipants / kpis.totalEvents) : 0}</div>
        </div>
      </div>

      {/* Trend de fotos últimos días */}
      <div className="card p-5 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-gray-800 font-medium"><Calendar className="w-5 h-5" /> Fotos últimos {trendDays} días</div>
        </div>
        <div className="h-40 flex items-end gap-2">
          {trend.labels.map((lab, i) => (
            <div key={lab} className="flex-1 flex flex-col items-center">
              <div className="w-full bg-indigo-200 rounded-t" style={{ height: `${Math.round((trend.counts[i] / maxTrend) * 100)}%` }} />
              <div className="text-[10px] text-gray-500 mt-1">{lab.slice(5)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Distribución por estado */}
      <div className="card p-5 mb-8">
        <div className="text-gray-800 font-medium mb-3">Estado de eventos</div>
        <div className="space-y-2">
          {(['active','inactive']).map((s) => {
            const c = statusCounts[s] || 0;
            const pct = Math.round((c / totalByStatus) * 100);
            const label = s === 'active' ? 'Activo' : 'Inactivo';
            return (
              <div key={s}>
                <div className="flex justify-between text-sm text-gray-600 mb-1"><span>{label}</span><span>{c}</span></div>
                <div className="h-2 bg-gray-100 rounded">
                  <div className={`h-2 rounded ${s==='active'?'bg-green-500':'bg-gray-400'}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top eventos por fotos */}
      <div className="card p-5">
        <div className="text-gray-800 font-medium mb-3">Top eventos por fotos</div>
        <div className="space-y-3">
          {topEvents.map((e) => {
            const max = Math.max(1, topEvents[0]?.totalPhotos || 1);
            const w = Math.round(((e.totalPhotos || 0) / max) * 100);
            return (
              <div key={e.eventId || e.id} className="">
                <div className="flex justify-between text-sm"><span className="font-medium text-gray-900 truncate pr-2" title={e.name}>{e.name}</span><span className="text-gray-600">{e.totalPhotos || 0}</span></div>
                <div className="h-2 bg-gray-100 rounded">
                  <div className="h-2 bg-indigo-500 rounded" style={{ width: `${w}%` }} />
                </div>
              </div>
            );
          })}
          {topEvents.length === 0 && (
            <div className="text-sm text-gray-600">No hay datos suficientes todavía.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
